
import { useState, useEffect } from 'react';
import { BASE_CSS }       from './styles/theme';
import { F1_CSS }         from './styles/f1theme';
import TabNav             from './components/TabNav';
import Settings, { applyTheme, loadSavedTheme } from './components/Settings';

// Football games
import HigherOrLower      from './games/HigherOrLower';
import WhoIsPlayer        from './games/WhoIsPlayer';
import TriviaGame         from './games/TriviaGame';
import FlagFlash          from './games/FlagFlash';
import YearGuesser        from './games/YearGuesser';
import ScorePredictor     from './games/ScorePredictor';
import Connections        from './games/Connections';
import MultiLobby         from './games/MultiLobby';
import PlayerPhone        from './games/PlayerPhone';
import PubQuizHost        from './games/PubQuizHost';
import LiveMatches        from './games/LiveMatches';
import PredictorHost      from './games/PredictorHost';
import PredictorPlayer    from './games/PredictorPlayer';

// F1
import F1Live             from './f1/F1Live';
import F1Predictor        from './f1/F1Predictor';
import F1PredictorPlayer  from './f1/F1PredictorPlayer';
import F1Standings        from './f1/F1Standings';
import F1Tracks           from './f1/F1Tracks';

import { TRIVIA_ROVERS, TRIVIA_FOREST } from './data/trivia';

const THEME_ROVERS = {
  name:'Blackburn Rovers',emoji:'🔵⚪',col:'#4ade80',titleClass:'green',pillClass:'g',
  fillClass:'prog-fill-g',hoverClass:'hv-g',btnClass:'rovers',panelClass:'rovers',
  headClass:'rovers',textClass:'rovers',eyebrow:'World Cup Trivia · Rovers Edition',
  grades:['Ewood Park Legend','Rovers Fanatic','Decent Blue & White','Fair-Weather Fan','Never Been to Ewood']
};
const THEME_FOREST = {
  name:'Nottingham Forest',emoji:'🔴⚪',col:'#ff6b52',titleClass:'red',pillClass:'r',
  fillClass:'prog-fill-r',hoverClass:'hv-r',btnClass:'forest',panelClass:'forest',
  headClass:'forest',textClass:'forest',eyebrow:'World Cup Trivia · Forest Edition',
  grades:['City Ground Legend','Forest Fanatic','Decent Red & White','Fair-Weather Fan','Never Been to the City Ground']
};

// Shell wraps every game with CSS always present
const Shell = ({children}) => (
  <div className="app">
    <style>{BASE_CSS}{F1_CSS}</style>
    {children}
  </div>
);

export default function App() {
  // All state declared first — before any useEffect that references them
  const [tab,setTab]                   = useState('football');
  const [showSettings,setShowSettings] = useState(false);
  const [game,setGame]                 = useState(null);
  const [best,setBest]                 = useState({});
  const [joinAsPlayer,setJoinAsPlayer] = useState(false);
  const [predictorMatch,setPredictorMatch] = useState(null);

  // Apply saved theme on first load
  useState(() => {
    const { themeId, fontSizeId } = loadSavedTheme();
    applyTheme(themeId, fontSizeId);
  });

  // Push a history entry whenever we navigate into a game so the
  // device back button returns to the menu instead of leaving the app
  useEffect(() => {
    if (game) {
      window.history.pushState({ game, tab }, '', window.location.pathname);
    }
  }, [game, tab]);

  useEffect(() => {
    const onPop = () => {
      if (game) {
        setGame(null);
        setJoinAsPlayer(false);
        setPredictorMatch(null);
        window.history.pushState({}, '', window.location.pathname);
      }
    };
    window.addEventListener('popstate', onPop);
    window.history.replaceState({}, '', window.location.pathname);
    return () => window.removeEventListener('popstate', onPop);
  }, [game]);

  const setBestFor = id => v => setBest(b=>({...b,[id]:Math.max(v,b[id]||0)}));
  const B          = id => best[id]||0;
  const back       = () => { setGame(null); setJoinAsPlayer(false); };
  const go         = id => setGame(id);

  /* ── ACTIVE GAME ROUTES ─────────────────────────────────────────── */
  // Football single player
  if(game==='hol')    return <Shell><HigherOrLower  onBack={back} best={B('hol')}   setBest={setBestFor('hol')}  /></Shell>;
  if(game==='face')   return <Shell><WhoIsPlayer    onBack={back} best={B('face')}  setBest={setBestFor('face')} /></Shell>;
  if(game==='flag')   return <Shell><FlagFlash      onBack={back} best={B('flag')}  setBest={setBestFor('flag')} /></Shell>;
  if(game==='year')   return <Shell><YearGuesser    onBack={back} best={B('year')}  setBest={setBestFor('year')} /></Shell>;
  if(game==='score')  return <Shell><ScorePredictor onBack={back} best={B('score')} setBest={setBestFor('score')}/></Shell>;
  if(game==='conn')   return <Shell><Connections    onBack={back} best={B('conn')}  setBest={setBestFor('conn')} /></Shell>;
  if(game==='rovers') return <Shell><TriviaGame onBack={back} best={B('rovers')} setBest={setBestFor('rovers')} data={TRIVIA_ROVERS} theme={THEME_ROVERS}/></Shell>;
  if(game==='forest') return <Shell><TriviaGame onBack={back} best={B('forest')} setBest={setBestFor('forest')} data={TRIVIA_FOREST} theme={THEME_FOREST}/></Shell>;
  // Football group
  if(game==='pqgeneral') return <Shell><PubQuizHost onBack={back} initialBank="general"/></Shell>;
  if(game==='pqrovers')  return <Shell><PubQuizHost onBack={back} initialBank="rovers"/></Shell>;
  if(game==='pqforest')  return <Shell><PubQuizHost onBack={back} initialBank="forest"/></Shell>;
  // Football predictor
  if(game==='liveMatches') return <Shell><LiveMatches onBack={back} onStartPredictor={m=>{setPredictorMatch(m);go('predictorHost');}}/></Shell>;
  if(game==='predictorHost')   return <Shell><PredictorHost   match={predictorMatch} onBack={back}/></Shell>;
  if(game==='predictorPlayer') return <Shell><PredictorPlayer onBack={back}/></Shell>;
  // Multiplayer
  if(game==='multi')  return <Shell>{joinAsPlayer?<PlayerPhone onBack={back}/>:<MultiLobby onBack={back} onJoinAsPlayer={()=>setJoinAsPlayer(true)}/>}</Shell>;
  // F1
  if(game==='f1live')        return <Shell><F1Live             onBack={back}/></Shell>;
  if(game==='f1predictor')   return <Shell><F1Predictor        onBack={back}/></Shell>;
  if(game==='f1predplayer')  return <Shell><F1PredictorPlayer  onBack={back}/></Shell>;
  if(game==='f1standings')   return <Shell><F1Standings        onBack={back}/></Shell>;
  if(game==='f1tracks')      return <Shell><F1Tracks           onBack={back}/></Shell>;

  /* ── MAIN MENU ───────────────────────────────────────────────────── */
  return (
    <div className="app">
      <style>{BASE_CSS}{F1_CSS}</style>

      {/* Brand header */}
      <div style={{textAlign:'center',padding:'18px 14px 0',background:'#080f1e'}}>
        <div className="menu-logo">🏆 THE SPORTS ARCADE</div>
        <div className="menu-sub">housemonk · games</div>
      </div>

      {/* Tab nav + settings button */}
      <div style={{display:'flex',alignItems:'stretch',background:'#080f1e',borderBottom:'2px solid var(--border,#1e3a6e)'}}>
        <div style={{flex:1}}><TabNav active={tab} onChange={t=>{setTab(t);setGame(null);}}/></div>
        <button onClick={()=>setShowSettings(true)}
          style={{padding:'0 14px',background:'transparent',border:'none',borderLeft:'1px solid var(--border,#1e3a6e)',
            color:'var(--sub,#8ba3cc)',cursor:'pointer',fontSize:'1.1rem',transition:'color 0.15s'}}
          title="Settings">⚙️</button>
      </div>
      {showSettings && <Settings onClose={()=>setShowSettings(false)}/>}

      {/* ── FOOTBALL TAB ── */}
      {tab==='football' && (
        <div className="menu layer" style={{paddingTop:16}}>

          <div className="menu-tier">
            <div className="menu-tier-hdr"><div className="menu-tier-line"/><div className="menu-tier-label">🎮 Single Player</div><div className="menu-tier-line"/></div>
            <div className="menu-cards">
              {[
                {id:'hol',   icon:'📊',title:'Higher or Lower',  desc:'FIFA Rankings — higher or lower?',                cls:'mc-sp',  hs:'Best: {s}'},
                {id:'face',  icon:'👤',title:"Who's the Player?",desc:'Blurred portrait — name the World Cup star',      cls:'mc-face',hs:'Best: {s}/12'},
                {id:'flag',  icon:'🚩',title:'Flag Flash',        desc:'4 seconds to name the country',                   cls:'mc-sp',  hs:'Best: {s}/15'},
                {id:'year',  icon:'📅',title:'When Was It?',      desc:'Guess the year of iconic WC moments',             cls:'mc-sp',  hs:'Best: {s} pts'},
                {id:'score', icon:'⚽',title:'Score Predictor',   desc:'Guess the score of classic WC matches',           cls:'mc-sp',  hs:'Best: {s} pts'},
                {id:'conn',  icon:'🔗',title:'WC Connections',    desc:'Find 4 groups of 4 — NYT style',                  cls:'mc-face',hs:'Best: {s}'},
                {id:'rovers',icon:'🔵',title:'Rovers Trivia',     desc:'WC quiz with Blackburn Rovers connections',       cls:'mc-rovers',hs:'Best: {s}/10'},
                {id:'forest',icon:'🔴',title:'Forest Trivia',     desc:'WC quiz with Nottingham Forest connections',      cls:'mc-forest',hs:'Best: {s}/10'},
              ].map(g=>(
                <button key={g.id} className={'menu-card '+g.cls} onClick={()=>go(g.id)}>
                  <div className="mc-icon">{g.icon}</div>
                  <div className="mc-title">{g.title}</div>
                  <div className="mc-desc">{g.desc}</div>
                  {B(g.id)>0&&<div className="mc-hs">{g.hs.replace('{s}',B(g.id))}</div>}
                </button>
              ))}
            </div>
          </div>

          <div className="menu-tier">
            <div className="menu-tier-hdr"><div className="menu-tier-line"/><div className="menu-tier-label">🔴 Live Match Predictor</div><div className="menu-tier-line"/></div>
            <div className="menu-cards">
              <button className="menu-card mc-mp" onClick={()=>go('liveMatches')}>
                <div className="mc-icon">🎯</div><div className="mc-title">Host Predictor</div>
                <div className="mc-desc">Pick a match, share a code. Players predict score, scorers, cards, corners. Live scoring throughout.</div>
              </button>
              <button className="menu-card mc-mp" onClick={()=>go('predictorPlayer')}>
                <div className="mc-icon">📱</div><div className="mc-title">Join Predictor</div>
                <div className="mc-desc">Join with a 4-letter code. Submit predictions, leave and rejoin anytime. Session lives 24h.</div>
              </button>
            </div>
          </div>

          <div className="menu-tier">
            <div className="menu-tier-hdr"><div className="menu-tier-line"/><div className="menu-tier-label">📺 Group / Pub Quiz</div><div className="menu-tier-line"/></div>
            <div className="menu-cards">
              <button className="menu-card mc-grp" onClick={()=>go('pqgeneral')}><div className="mc-icon">🌍</div><div className="mc-title">World Cup Quiz</div><div className="mc-desc">Host reads, marks teams ✓/✗. Up to 8 teams.</div></button>
              <button className="menu-card mc-rovers" onClick={()=>go('pqrovers')}><div className="mc-icon">🔵⚪</div><div className="mc-title">Rovers Quiz</div><div className="mc-desc">WC questions with Rovers connections.</div></button>
              <button className="menu-card mc-forest" onClick={()=>go('pqforest')}><div className="mc-icon">🔴⚪</div><div className="mc-title">Forest Quiz</div><div className="mc-desc">WC questions with Forest connections.</div></button>
            </div>
          </div>
        </div>
      )}

      {/* ── F1 TAB ── */}
      {tab==='f1' && (
        <div className="menu layer" style={{paddingTop:16}}>
          <div className="menu-tier">
            <div className="menu-tier-hdr"><div className="menu-tier-line"/><div className="menu-tier-label" style={{color:'#e8002d'}}>🏁 Live Data</div><div className="menu-tier-line"/></div>
            <div className="menu-cards">
              <button className="menu-card mc-f1" onClick={()=>go('f1live')}>
                <div className="mc-icon">🏁</div><div className="mc-title">Live Session</div>
                <div className="mc-desc">Real-time leaderboard, gaps, pit stops, weather, race control messages. Browse any session from this season.</div>
              </button>
              <button className="menu-card mc-f1s" onClick={()=>go('f1standings')}>
                <div className="mc-icon">📊</div><div className="mc-title">Standings</div>
                <div className="mc-desc">2026 season driver and constructor standings, updated live after each race.</div>
              </button>
              <button className="menu-card mc-f1s" onClick={()=>go('f1tracks')}>
                <div className="mc-icon">🗺️</div><div className="mc-title">Tracks</div>
                <div className="mc-desc">Every 2026 circuit with track outline, session schedule and results. Filter by month.</div>
              </button>
            </div>
          </div>

          <div className="menu-tier">
            <div className="menu-tier-hdr"><div className="menu-tier-line"/><div className="menu-tier-label" style={{color:'#e8002d'}}>🎯 Race Predictor</div><div className="menu-tier-line"/></div>
            <div className="menu-cards">
              <button className="menu-card mc-f1" onClick={()=>go('f1predictor')}>
                <div className="mc-icon">🎯</div><div className="mc-title">Host Predictor</div>
                <div className="mc-desc">Pick a session, share a code. Players predict winner, podium, fastest lap, safety car, pit stops. Live scoring.</div>
              </button>
              <button className="menu-card mc-f1s" onClick={()=>go('f1predplayer')}>
                <div className="mc-icon">📱</div><div className="mc-title">Join Predictor</div>
                <div className="mc-desc">Join an F1 predictor session with a code. Leave and rejoin — predictions persist for 24h.</div>
              </button>
            </div>
          </div>

          <div style={{textAlign:'center',fontSize:'0.6rem',color:'#3a1a1a',padding:'8px 0'}}>
            Live data via OpenF1 (openf1.org) · No API key required
          </div>
        </div>
      )}

      {/* ── MULTIPLAYER TAB ── */}
      {tab==='multi' && (
        <div className="menu layer" style={{paddingTop:16}}>
          <div className="menu-tier">
            <div className="menu-tier-hdr"><div className="menu-tier-line"/><div className="menu-tier-label">📱 Phone-Join Games</div><div className="menu-tier-line"/></div>
            <div className="menu-cards">
              <button className="menu-card mc-mp" onClick={()=>go('multi')}>
                <div className="mc-icon">🚩</div><div className="mc-title">Flag Flash Multi</div>
                <div className="mc-desc">Host creates a session, players join on phones. Fastest correct answer wins each round. 15pts first, 10pts others.</div>
              </button>
            </div>
          </div>

          <div className="menu-tier">
            <div className="menu-tier-hdr"><div className="menu-tier-line"/><div className="menu-tier-label">🎯 Join a Predictor Session</div><div className="menu-tier-line"/></div>
            <div className="menu-cards">
              <button className="menu-card mc-mp" onClick={()=>go('predictorPlayer')}>
                <div className="mc-icon">⚽</div><div className="mc-title">Football Predictor</div>
                <div className="mc-desc">Join a football match predictor session. Enter your 4-letter code.</div>
              </button>
              <button className="menu-card mc-f1" onClick={()=>go('f1predplayer')}>
                <div className="mc-icon">🏎️</div><div className="mc-title">F1 Predictor</div>
                <div className="mc-desc">Join an F1 race predictor session. Enter your 4-letter code.</div>
              </button>
            </div>
          </div>

          <div style={{textAlign:'center',padding:'20px 14px'}}>
            <p style={{fontSize:'0.7rem',color:'#4a6a9e',lineHeight:1.7}}>
              All predictor sessions persist for <strong style={{color:'#e8edf8'}}>24 hours</strong>.<br/>
              Leave and rejoin at any time with the same code.<br/>
              Predictions are saved even when you disconnect.
            </p>
          </div>
        </div>
      )}

      <div style={{textAlign:'center',fontSize:'0.52rem',color:'#1a2a3e',padding:'12px 0 20px',letterSpacing:'0.15em'}}>
        THE SPORTS ARCADE · HOUSEMONK · {new Date().getFullYear()}
      </div>
    </div>
  );
}
